import { AkpError } from '@akpl/shared';
import { useCallback, useRef, useState } from 'react';

import { api } from '../api';
import { useApiErrorToast } from '../apiError';
import { useRouter } from '../router';
import { selectSid, useStore } from '../store';
import { useT } from '../useT';

export interface UseQueueActionsOptions {
  /**
   * Called after a successful `play-now` instead of navigating. The queue drawer lives inside
   * the Player (docs/04 §7.6), so it switches the current item rather than pushing a route.
   */
  onPlayed?: (itemId: string, tone: string) => void;
}

/**
 * How long a queue action may take before the drawer gives up and toasts `timeout`; 25 s for the
 * same reason as `SONG_ACTION_TIMEOUT_MS` (a removal took 5.9 s, all of it in RTDB).
 */
export const QUEUE_ACTION_TIMEOUT_MS = 25_000;

export interface QueueActions {
  /** The item an action is in flight for (drives the row's spinner), or null. */
  pendingId: string | null;
  /** `POST /sessions/:sid/queue/:itemId/play-now`, then open/switch the player to that item. */
  playNow(itemId: string, tone: string): void;
  /** `DELETE /sessions/:sid/queue/:itemId`. */
  remove(itemId: string): void;
  /** `POST /sessions/:sid/player/command`. */
  sendCommand(type: 'play' | 'pause' | 'next' | 'stop'): void;
}

/**
 * Marks a queue row as `played` locally. The RTDB listener owns the queue (docs/02 §2), but a
 * `409 conflict` is the API telling us this row is behind, and the drawer must not keep
 * offering an action the API refuses.
 */
function markPlayed(itemId: string): void {
  const item = useStore.getState().queue.items[itemId];
  if (!item || item.status === 'played') return;
  useStore.getState().upsertQueueItem(itemId, { ...item, status: 'played' });
}

/**
 * The queue drawer's API calls (docs/03 §2 "Sessions & queue"). Every mutation goes through
 * the API — the TV never writes the queue itself (docs/02 §2) — and the RTDB listener is what
 * refreshes the rows.
 *
 * Calls are serialized behind one in-flight flag: a D-pad Enter is easy to repeat and a second
 * `play-now` on the same item would restart it.
 */
export function useQueueActions({ onPlayed }: UseQueueActionsOptions = {}): QueueActions {
  const { t } = useT();
  const sid = useStore(selectSid);
  const showToast = useStore((s) => s.showToast);
  const navigate = useRouter((s) => s.navigate);
  const toastError = useApiErrorToast();
  const busy = useRef(false);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const run = useCallback(
    (fn: (sid: string) => Promise<void>, itemId: string | null = null) => {
      if (!sid || busy.current) return;
      busy.current = true;
      setPendingId(itemId);
      void fn(sid)
        .catch((error: unknown) => toastError(error))
        .finally(() => {
          busy.current = false;
          setPendingId(null);
        });
    },
    [sid, toastError],
  );

  const playNow = useCallback<QueueActions['playNow']>(
    (itemId, tone) =>
      run(async (session) => {
        try {
          await api.playNowItem({
            params: { sid: session, itemId },
            timeoutMs: QUEUE_ACTION_TIMEOUT_MS,
          });
        } catch (error) {
          // `409 conflict` = the row is stale: the item reached `played` while the drawer
          // still offered "Reproducir" on it. Reconcile the local queue with what the API
          // just said so the row moves to "Ya cantadas" and stops accepting Enter; the toast
          // then explains it instead of the generic failure (QA2 TV-65).
          if (AkpError.is(error) && error.code === 'conflict') markPlayed(itemId);
          throw error;
        }
        if (onPlayed) onPlayed(itemId, tone);
        else navigate('player', { itemId, tone });
      }, itemId),
    [run, navigate, onPlayed],
  );

  const remove = useCallback<QueueActions['remove']>(
    (itemId) =>
      run(async (session) => {
        await api.removeQueueItem({
          params: { sid: session, itemId },
          timeoutMs: QUEUE_ACTION_TIMEOUT_MS,
        });
        // The listener will drop it too; doing it here keeps the row from lingering for a
        // frame (and makes the action visible in offline-auth dev, where nothing echoes back).
        useStore.getState().removeQueueItem(itemId);
        showToast(t('tv:queue.removed'), 'success');
      }, itemId),
    [run, showToast, t],
  );

  const sendCommand = useCallback<QueueActions['sendCommand']>(
    (type) =>
      run(async (session) => {
        await api.playerCommand({ params: { sid: session }, body: { type } });
      }),
    [run],
  );

  return { pendingId, playNow, remove, sendCommand };
}
